'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { getTranslations, type PageTranslations } from '@/lib/i18n/translations';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',    nativeLabel: 'English'    },
  { code: 'es', label: 'Spanish',    nativeLabel: 'Español'    },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português'  },
  { code: 'zh', label: 'Chinese',    nativeLabel: '中文'        },
  { code: 'ja', label: 'Japanese',   nativeLabel: '日本語'      },
  { code: 'ko', label: 'Korean',     nativeLabel: '한국어'      },
  { code: 'hi', label: 'Hindi',      nativeLabel: 'हिन्दी'     },
  { code: 'te', label: 'Telugu',     nativeLabel: 'తెలుగు'     },
  { code: 'ta', label: 'Tamil',      nativeLabel: 'தமிழ்'      },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const LANGUAGE_KEY = 'languagePreference';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: PageTranslations;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLang] = useState<SupportedLanguage>('en');
  const [t, setT] = useState<PageTranslations>(getTranslations('en'));

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LANGUAGE_KEY) as SupportedLanguage | null;
      if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
        setLang(saved);
        setT(getTranslations(saved));
      }
    } catch {}
  }, []);

  const setLanguage = (lang: SupportedLanguage) => {
    setLang(lang);
    setT(getTranslations(lang));
    try {
      localStorage.setItem(LANGUAGE_KEY, lang);
    } catch {}
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
